import React from 'react';
import { Outlet, useParams } from 'react-router-dom';
import useWidgetLayoutQuery from '../hooks/useWidgetLayoutQuery';
import DockRenderer from './DockRenderer';
import DownloadInfoCard from '../../DownloadInfoCard';
import HeadingFilter from '../../HeadingFilter';
import DownloadInfo from '../../OrgDim/DownloadInfo';
import PageHeading from '../../PageHeading';
import { useDeviceType } from '../../../../utilities/common/deviceType';
import { FEATURED_DOWNLOADS } from '../constants';
import { getWidgetsLayoutContentStyle } from '../utils';
import useFilters from '../../../../utilities/hooks/useFilters';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import { getDateFilterWindow } from '../../../../utilities/common/date';

type Props = {
  isSidebarCollapsed?: boolean;
};

function WidgetsLayoutRenderer({ isSidebarCollapsed }: Props) {
  const { slug } = useParams();
  const { data, refetch, isPending } = useWidgetLayoutQuery(slug);
  const { setDateRange, selectedDate } = useFilters();
  const { configData } = useFilterData();

  const isMobile = useDeviceType();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const lastUpdatedAt = data?.meta?.lastUpdatedAt;
  const contractStartDate = configData?.contractStartDate;

  React.useEffect(() => {
    setDateRange(
      getDateFilterWindow({
        monthOffset: selectedDate.value,
        contractDate: contractStartDate,
        filterAvailableTill: lastUpdatedAt,
      }),
    );
  }, [slug, lastUpdatedAt, contractStartDate]);

  const contentStyle = getWidgetsLayoutContentStyle({
    isMobile,
    isSidebarCollapsed,
  });

  return (
    <div
      className="hrdb-body-container bg-taupe-50 content-transition relative flex flex-1 flex-col p-6"
      style={contentStyle}
    >
      <div
        className={`hrdb-body-container bg-taupe-50 content-transition relative flex flex-1 flex-col p-6`}
      >
        <PageHeading
          title={data.title}
          subTitle={data.subtitle}
          refreshWidget={refetch}
          meta={data?.meta}
          showBackButton={
            slug?.startsWith('dimensions-') && slug !== 'dimensions-legacy'
          }
        />

        {slug === 'dimensions-legacy' && (
          <div className="mt-4 mb-6 grid w-full gap-y-2 pb-6 lg:mb-0 lg:flex lg:gap-x-5">
            {FEATURED_DOWNLOADS.map((download) => (
              <DownloadInfoCard
                key={download.title}
                title={download.title}
                subTitle={download.subTitle}
                link={download.link}
                color={download.color}
                event={download.instrumentation}
              />
            ))}
          </div>
        )}

        <HeadingFilter
          loading={isPending}
          meta={data?.meta}
          layoutSubtitle={data?.subtitle}
        />

        {slug === 'dimensions' && <DownloadInfo />}

        <div className="flex flex-col gap-6">
          {data.docks.map((dock, index) => (
            <DockRenderer key={dock.id} position={index} dock={dock} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default WidgetsLayoutRenderer;
